import type {
  DialogCloseProps,
  DialogContentProps,
  DialogDescriptionProps,
  DialogFooterProps,
  DialogHeaderProps,
  DialogRootProps,
  DialogTitleProps,
  DialogTriggerProps,
} from '#src/base/dialog/dialog.tsx';
import { Dialog } from '#src/base/dialog/dialog.tsx';
import type { DrawerContentProps } from '#src/base/drawer/drawer.tsx';
import { Drawer } from '#src/base/drawer/drawer.tsx';
import { cn } from '#src/styling/cn.ts';
import { useShouldAdaptToDrawer } from '#src/styling/use_should_adapt_to_drawer.ts';
import { createContext, use } from 'react';
const AdaptiveDialogContext = createContext(false);

function useAdaptiveDialogContext() {
  return use(AdaptiveDialogContext);
}

export type AdaptiveDialogRootProps = DialogRootProps & {
  shouldAdaptToDrawer?: boolean;
};

const AdaptiveDialogRoot = ({
  shouldAdaptToDrawer: shouldAdaptToDrawerProp,
  ...props
}: AdaptiveDialogRootProps) => {
  const shouldAdaptToDrawerByViewport = useShouldAdaptToDrawer();
  const shouldAdaptToDrawer = shouldAdaptToDrawerProp ?? shouldAdaptToDrawerByViewport;

  return (
    <AdaptiveDialogContext value={shouldAdaptToDrawer}>
      {shouldAdaptToDrawer ? <Drawer.Root {...props} /> : <Dialog.Root {...props} />}
    </AdaptiveDialogContext>
  );
};

export type AdaptiveDialogTriggerProps = DialogTriggerProps;

const AdaptiveDialogTrigger = (props: AdaptiveDialogTriggerProps) => {
  const shouldAdaptToDrawer = useAdaptiveDialogContext();
  return shouldAdaptToDrawer ? <Drawer.Trigger {...props} /> : <Dialog.Trigger {...props} />;
};

export type AdaptiveDialogCloseProps = DialogCloseProps;

const AdaptiveDialogClose = (props: AdaptiveDialogCloseProps) => {
  const shouldAdaptToDrawer = useAdaptiveDialogContext();
  return shouldAdaptToDrawer ? <Drawer.Close {...props} /> : <Dialog.Close {...props} />;
};

export type AdaptiveDialogContentProps = DialogContentProps &
  Pick<DrawerContentProps, 'showHandle' | 'popupClassName'>;

const AdaptiveDialogContent = ({
  className,
  popupClassName,
  showHandle,
  ...props
}: AdaptiveDialogContentProps) => {
  const shouldAdaptToDrawer = useAdaptiveDialogContext();

  if (shouldAdaptToDrawer) {
    return (
      <Drawer.Content
        className={cn('p-0', className)}
        popupClassName={popupClassName}
        showHandle={showHandle}
        showCloseButton={false}
        {...props}
      />
    );
  }

  return <Dialog.Content className={cn('p-0', className)} {...props} />;
};

export type AdaptiveDialogHeaderProps = DialogHeaderProps;

const AdaptiveDialogHeader = (props: AdaptiveDialogHeaderProps) => {
  const shouldAdaptToDrawer = useAdaptiveDialogContext();
  return shouldAdaptToDrawer ? <Drawer.Header {...props} /> : <Dialog.Header {...props} />;
};

export type AdaptiveDialogFooterProps = DialogFooterProps;

const AdaptiveDialogFooter = (props: AdaptiveDialogFooterProps) => {
  const shouldAdaptToDrawer = useAdaptiveDialogContext();
  return shouldAdaptToDrawer ? <Drawer.Footer {...props} /> : <Dialog.Footer {...props} />;
};

export type AdaptiveDialogTitleProps = DialogTitleProps;

const AdaptiveDialogTitle = (props: AdaptiveDialogTitleProps) => {
  const shouldAdaptToDrawer = useAdaptiveDialogContext();
  return shouldAdaptToDrawer ? <Drawer.Title {...props} /> : <Dialog.Title {...props} />;
};

export type AdaptiveDialogDescriptionProps = DialogDescriptionProps;

const AdaptiveDialogDescription = (props: AdaptiveDialogDescriptionProps) => {
  const shouldAdaptToDrawer = useAdaptiveDialogContext();
  return shouldAdaptToDrawer ? (
    <Drawer.Description {...props} />
  ) : (
    <Dialog.Description {...props} />
  );
};

export const AdaptiveDialog = {
  Root: AdaptiveDialogRoot,
  Trigger: AdaptiveDialogTrigger,
  Close: AdaptiveDialogClose,
  Content: AdaptiveDialogContent,
  Header: AdaptiveDialogHeader,
  Footer: AdaptiveDialogFooter,
  Title: AdaptiveDialogTitle,
  Description: AdaptiveDialogDescription,
};
